import React, { useEffect } from "react";
import {
  SecondaryGrid,
  GridColumn,
  useGrid,
  useFlyoutContainer,
  GridRecordClickEventArgs,
  flyoutCloseEventAtom,
} from "@ucl/library";
import { Predicate } from "@syncfusion/ej2-data";
import variables from "@/config/variables";
import { SanctionsMatchODataDTO } from "@/types/SanctionsMatchODataDTO";
import "./SanctionsMatchesGrid.scss";
import { useAtom } from "jotai";

interface SanctionsMatchesGridProps {
  parentEntityId?: string;
  parentEntityType?: string;
  financialApplicationId?: string;
  getAuthRequestHeader: () => Promise<Record<string, string>>;
}

export const SanctionsMatchesGrid: React.FC<SanctionsMatchesGridProps> = ({
  parentEntityId,
  parentEntityType,
  financialApplicationId,
  getAuthRequestHeader,
}) => {
  const { navigateToFlyout } = useFlyoutContainer();
  const [closeEvent] = useAtom(flyoutCloseEventAtom);

  const gridKey = "sanctions-matches-grid";
  const flyoutKey = "sanctions-match-flyout";

  const getPredicate = (parentEntityTypeFilter?: string, parentEntityIdFilter?: string) => {
    if (!parentEntityTypeFilter && !parentEntityIdFilter) {
      return undefined;
    }

    let predicate: Predicate | undefined;

    if (parentEntityTypeFilter) {
      predicate = new Predicate("parentEntityType", "equal", parentEntityTypeFilter);
    }

    if (parentEntityIdFilter) {
      const parentEntityIdPredicate = new Predicate(
        "parentEntityId",
        "equal",
        `'${parentEntityIdFilter}'`
      );
      if (predicate) {
        predicate = predicate.and(parentEntityIdPredicate);
      } else {
        predicate = parentEntityIdPredicate;
      }
    }

    return predicate;
  };

  // Basic columns configuration - using type assertion for now
  const columnsConfiguration: GridColumn[] = [
    new GridColumn({
      field: "screenedName",
      headerText: "Screened Name",
      type: "string",
    }),
    new GridColumn({
      field: "entityType",
      headerText: "Entity Type",
      type: "string",
    }),
    new GridColumn({
      field: "searchStatus_AsString",
      headerText: "Search Status",
      type: "string",
    }),
    new GridColumn({
      field: "matchResult_AsString",
      headerText: "Match Result",
      type: "string",
    }),
    new GridColumn({
      field: "topOpenSanctionsMatch_Match",
      headerText: "Matched?",
      type: "boolean",
    }),
    new GridColumn({
      field: "topOpenSanctionsMatch_EntityId",
      headerText: "Matched Entity ID",
      type: "string",
    }),
    new GridColumn({
      field: "topOpenSanctionsMatch_EntityCaption",
      headerText: "Matched Entity Caption",
      type: "string",
    }),
    new GridColumn({
      field: "topOpenSanctionsMatch_Score",
      headerText: "Score",
      type: "number",
    }),
    new GridColumn({
      field: "searchErrorReason",
      headerText: "Search Error Reason",
      type: "string",
    }),
  ];

  const handleRecordClick = (args: GridRecordClickEventArgs<SanctionsMatchODataDTO>) => {
    navigateToFlyout(flyoutKey, {
      id: args.rowData.id,
      financialApplicationId: financialApplicationId ?? "",
      entityId: args.rowData.entityId ?? "",
      entityType: args.rowData.entityType ?? "",
    });
  };

  useEffect(() => {
    if (closeEvent && closeEvent.key === flyoutKey) {
      gridRef.current?.refresh();
    }
  }, [closeEvent]);

  // Use the useGrid hook with proper typing
  const { ready, gridRef, gridSettings } = useGrid<SanctionsMatchODataDTO>({
    gridKey: gridKey,
    columnsConfiguration: columnsConfiguration,
    dataSource: `${variables.apiBaseUrl}/odata/sanctionsmatchesodata`,
    getAuthRequestHeader: getAuthRequestHeader,
    predicate: getPredicate(parentEntityType, parentEntityId),
    gridConfiguration: {
      onRecordClick: handleRecordClick,
    },
  });

  return (
    <div className="sanctions-matches-grid">
      <SecondaryGrid ready={ready} gridRef={gridRef} gridSettings={gridSettings} />
    </div>
  );
};
